"use client";

/**
 * DashboardImage — sibling to <DashboardView>.
 *
 * Renders the single-PNG alert dashboard produced by PKA's
 * dashboard_image_generator (OpenRouter → openai/gpt-5.4-image-2).
 * Called by every surface that currently renders <DashboardView> when
 * the backend tells us the alert has an `image_url` — otherwise the
 * caller still uses <DashboardView> with the iframe'd HTML.
 *
 * Why a new component (not a branch inside DashboardView):
 *   - <DashboardView> mutates the HTML string and sets up postMessage
 *     channels for height reporting + resize — none of which apply to
 *     a static image.
 *   - Memoization + prop shape differ. DashboardView keys off `html`;
 *     DashboardImage keys off `url`.
 *   - Isolating the renderer keeps the legacy iframe path untouched so
 *     a bug in the image path can never break the HTML fallback.
 */

import { memo, useCallback, useState } from "react";
import { ImageOff } from "lucide-react";
import { alertLog } from "@/lib/alert-display-log";

interface DashboardImageProps {
  url: string;
  /** Optional: generated_at ISO string for the hover tooltip. Purely informational. */
  generatedAt?: string | null;
  /** Optional: model slug that produced this image (e.g. "openai/gpt-5.4-image-2").
   *  Shown in the hover tooltip for debugging. */
  model?: string | null;
  /** Drop the card border / rounded-corner shell. Matches DashboardView's API. */
  noBorder?: boolean;
  /** Called when the <img> load event fires. Useful for hero shells that
   *  want to fade from skeleton to image without arming postMessage. */
  onLoad?: () => void;
}

function DashboardImageComponent({
  url,
  generatedAt,
  model,
  noBorder = false,
  onLoad,
}: DashboardImageProps) {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);

  const handleLoad = useCallback(() => {
    setLoaded(true);
    alertLog("dashboard:image_loaded", { url: url.slice(0, 120), model });
    onLoad?.();
  }, [url, model, onLoad]);

  const handleError = useCallback(() => {
    setError(true);
    alertLog("dashboard:image_error", { url: url.slice(0, 120), model });
  }, [url, model]);

  const wrapperClass = noBorder
    ? "w-full max-w-full relative"
    : "w-full max-w-full rounded-lg overflow-hidden bg-slate-950/70 border border-slate-400/15 relative";

  if (error) {
    return (
      <div className={wrapperClass} style={{ minHeight: 240 }}>
        <div className="flex flex-col items-center justify-center gap-2 py-12 text-slate-400">
          <ImageOff className="w-6 h-6" strokeWidth={1.8} />
          <div className="text-xs font-mono uppercase tracking-wider">
            dashboard image unavailable
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={wrapperClass}>
      {/* Skeleton shimmer until image decodes.
       *
       * The iframe'd HTML dashboard reports its own height via postMessage
       * so <DashboardView>'s container can grow dynamically. Static images
       * don't need that dance — the <img> lays out naturally at
       * intrinsic-width-constrained-by-container and the parent can
       * reserve space with a min-height skeleton below. */}
      {!loaded && (
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "linear-gradient(110deg, rgba(30,41,59,0.0) 30%, rgba(56,189,248,0.08) 50%, rgba(30,41,59,0.0) 70%)",
            backgroundSize: "200% 100%",
            animation: "dashboard-image-shimmer 1.8s linear infinite",
          }}
        />
      )}
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={url}
        alt="Alert dashboard"
        title={
          generatedAt
            ? `Generated ${generatedAt}${model ? ` · ${model}` : ""}`
            : undefined
        }
        loading="lazy"
        decoding="async"
        onLoad={handleLoad}
        onError={handleError}
        style={{
          display: "block",
          width: "100%",
          height: "auto",
          opacity: loaded ? 1 : 0,
          transition: "opacity 250ms ease-out",
        }}
      />
      <style jsx>{`
        @keyframes dashboard-image-shimmer {
          0% {
            background-position: -50% 0;
          }
          100% {
            background-position: 150% 0;
          }
        }
      `}</style>
    </div>
  );
}

export const DashboardImage = memo(DashboardImageComponent);
